import React, { useEffect, useState } from 'react';

import { Button, Popover, TextFieldProps } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { getHours, getMinutes, isValid, startOfMinute } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useDebounce } from 'hooks/useDebounce';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { DateObject } from 'react-multi-date-picker';
import { Calendar } from 'shared/mui/Calendar/Calendar';
import { DatePickerActions } from 'shared/mui/DatePicker/components/DatePickerActions/DatePickerActions';
import { DatePickerMaskInput } from 'shared/mui/DatePicker/components/DatePickerMaskInput/DatePickerMaskInput';
import {
  DEFAULT_SHORTCUTS,
  Shortcuts,
} from 'shared/mui/DatePicker/components/DatePickerShortcuts/constants/shortcuts';
import { DatePickerShortcuts } from 'shared/mui/DatePicker/components/DatePickerShortcuts/DatePickerShortcuts';
import { DatePickerTime } from 'shared/mui/DatePicker/components/DatePickerTime/DatePickerTime';
import { DatePickerTimezoneToolbar } from 'shared/mui/DatePicker/components/DatePickerTimezoneToolbar/DatePickerTimezoneToolbar';
import {
  DEFAULT_DATE_TIME_MASK,
  DEFAULT_DATE_TIME_RANGE_MASK,
  DEFAULT_DATE_TIME_RANGE_PLACEHOLDER,
  DEFAULT_SEPARATOR,
  DEFAULT_TIMEZONE,
} from 'shared/mui/DatePicker/config/defaultValues';
import {
  PopoverCalendarContainerStyled,
  PopoverContentStyled,
  PopoverDatesStyled,
  PopoverLayoutStyled,
  PopoverListStyled,
  PopoverTimesStyled,
} from 'shared/mui/DatePicker/DatePicker.styled';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { formatInputToDateObject } from 'shared/mui/DatePicker/utils/formatInputToDateObject';
import { formatRangeInputToPlaceholder } from 'shared/mui/DatePicker/utils/formatRangeInputToPlaceholder';
import { DAY_END, DAY_START, setZonedDate } from 'shared/mui/DatePicker/utils/setZonedDate';

export type DateRangePickerValue = number | null;
export type DateRangeTimezoneValue = string | null;
export type DateRangeValue = [DateRangePickerValue, DateRangePickerValue];

export type DateTimeRangePickerProps = Omit<
  TextFieldProps,
  'value' | 'onChange' | 'defaultValue'
> & {
  minDate?: Date | number;
  maxDate?: Date | number;
  value: DateRangeValue;
  timezone?: DateRangeTimezoneValue;
  displayShortcuts?: boolean;
  defaultValue?: [number, number];
  onChange?: (newValue: DateRangeValue) => void;
  onChangeTimezone?: (newTimezone: DateRangeTimezoneValue) => void;
  shortcuts?: Shortcuts;
};

export const DateTimeRangeTimezonePicker: React.FC<DateTimeRangePickerProps> = ({
  label,
  variant = 'outlined',
  displayShortcuts = true,
  disabled,
  onChange: onChangeProp,
  onChangeTimezone: onChangeTimezoneProp,
  minDate,
  maxDate,
  value,
  timezone = null,
  defaultValue,
  shortcuts = DEFAULT_SHORTCUTS,
  size = 'small',
  ...restProps
}) => {
  const translate = useTypedTranslate();
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();
  const [error, setError] = useState(false);
  const [timezoneValue, setTimezoneValue] = useState<DateRangeTimezoneValue>(timezone);
  const [resetValue, setResetValue] = useState<DateRangeValue>(value || defaultValue);
  const [resetTimezone, setResetTimezone] = useState<DateRangeTimezoneValue>(timezone);

  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);

  const [calendarValue, setCalendarValue] = useState<(DateObject | null)[]>([null, null]);

  const [inputValue, setInputValue] = useState<string | undefined>();

  const open = Boolean(anchorEl);
  const id = open ? 'date-time-picker-popover' : undefined;

  useEffect(() => {
    setResetValue(value);
    setResetTimezone(timezone);
    setTimezoneValue(timezone);

    if (defaultValue && value.some((el) => !el)) {
      setStartTime(startOfMinute(new Date(defaultValue[0])));
      setEndTime(startOfMinute(new Date(defaultValue[1])));
      setInputValue(
        formatRangeInputToPlaceholder([defaultValue[0], defaultValue[1]], {
          format: DEFAULT_DATE_TIME_MASK,
          timezone,
        }),
      );
      setCalendarValue([
        formatInputToDateObject(defaultValue[0], timezone),
        formatInputToDateObject(defaultValue[1], timezone),
      ]);
      return;
    }

    if (value[0] || value[1]) {
      setStartTime(value[0] ? startOfMinute(new Date(value[0])) : null);
      setEndTime(value[1] ? startOfMinute(new Date(value[1])) : null);
      setCalendarValue([
        formatInputToDateObject(value[0], timezone),
        formatInputToDateObject(value[1], timezone),
      ]);

      setInputValue(
        formatRangeInputToPlaceholder([value[0], value[1]], {
          format: DEFAULT_DATE_TIME_MASK,
          timezone,
        }),
      );
      return;
    }
    setStartTime(null);
    setEndTime(null);
    setCalendarValue([null, null]);
    setInputValue('');
  }, [value, timezone]);

  const debouncedOnChangeInputHandler = useDebounce((target: string) => {
    const [start, end] = target.split(DEFAULT_SEPARATOR);

    if (!start && !end) {
      setError(false);
      setInputValue(undefined);
      return;
    }

    const [startDate, startTime] = start.split(' ');
    const [endDate, endTime] = end.split(' ');

    setError(true);
    const validStartDate = formatInputToDateObject(startDate);
    const validEndDate = formatInputToDateObject(endDate);

    if (validStartDate) {
      const newValue = setZonedDate({
        timezone: timezoneValue,
        date: validStartDate,
        time: startTime,
      });
      setError(false);
      setCalendarValue((prev) => [validStartDate, prev[1]]);
      setStartTime(newValue);
    } else {
      setCalendarValue((prev) => [null, prev[1]]);
      setStartTime(null);
    }

    if (validEndDate) {
      const newValue = setZonedDate({
        timezone: timezoneValue,
        date: validEndDate,
        time: endTime,
      });
      setError(false);
      setCalendarValue((prev) => [prev[0], validEndDate]);
      setEndTime(newValue);
    } else {
      setCalendarValue((prev) => [prev[0], null]);
      setEndTime(null);
    }

    setInputValue(target);
  }, 300);

  function onChangeInput(event: React.ChangeEvent<HTMLInputElement>) {
    debouncedOnChangeInputHandler(event?.target?.value);
  }

  function onChangeCalendar(value: (DateObject | null)[]) {
    setCalendarValue(value);

    const [start, end] = value;

    const validStart =
      start && startTime
        ? setZonedDate({
            timezone: timezoneValue,
            date: start,
            baseDate: startTime,
            time: DAY_START,
          })
        : setZonedDate({
            timezone: timezoneValue,
            date: start || new DateObject(),
            time: DAY_START,
          });

    const validEnd =
      end && endTime
        ? setZonedDate({
            timezone: timezoneValue,
            date: end,
            baseDate: endTime,
            time: DAY_END,
          })
        : setZonedDate({
            timezone: timezoneValue,
            date: end || new DateObject(),
            time: DAY_END,
          });

    setStartTime(validStart);
    setEndTime(validEnd);

    const validValue: DateRangeValue = [validStart.valueOf(), validEnd.valueOf()];

    setInputValue(
      formatRangeInputToPlaceholder(validValue, {
        format: DEFAULT_DATE_TIME_MASK,
        timezone: timezoneValue,
      }),
    );
  }

  const onChangeStartTime = (value: Date) => {
    if (!isValid(value)) {
      setInputValue(
        formatRangeInputToPlaceholder([null, endTime?.valueOf() || null], {
          format: DEFAULT_DATE_TIME_MASK,
          timezone: timezoneValue,
        }),
      );

      return;
    }

    const newStart = setZonedDate({
      timezone: timezoneValue,
      date: calendarValue[0] || new DateObject(),
      time: `${getHours(value)}:${getMinutes(value)}`,
    });

    setStartTime(newStart);
    setInputValue(
      formatRangeInputToPlaceholder([newStart.valueOf(), calendarValue[1]?.valueOf() || null], {
        format: DEFAULT_DATE_TIME_MASK,
        timezone: timezoneValue,
      }),
    );
  };

  const onChangeEndTime = (value: Date) => {
    if (!isValid(value)) {
      setInputValue(
        formatRangeInputToPlaceholder([startTime?.valueOf() || null, null], {
          format: DEFAULT_DATE_TIME_MASK,
          timezone: timezoneValue,
        }),
      );

      return;
    }

    const newEnd = setZonedDate({
      timezone: timezoneValue,
      date: calendarValue[1] || new DateObject(),
      time: `${getHours(value)}:${getMinutes(value)}`,
    });

    setEndTime(newEnd);
    setInputValue(
      formatRangeInputToPlaceholder([startTime?.valueOf() || null, newEnd.valueOf()], {
        format: DEFAULT_DATE_TIME_MASK,
        timezone: timezoneValue,
      }),
    );
  };

  const onClickShortcut = (value: DateRangeValue) => {
    const [startDate, endDate] = value;
    setStartTime(startDate ? startOfMinute(new Date(startDate)) : null);
    setEndTime(endDate ? startOfMinute(new Date(endDate)) : null);
    setInputValue(
      formatRangeInputToPlaceholder(value, {
        format: DEFAULT_DATE_TIME_MASK,
        timezone: timezoneValue,
      }),
    );
    setCalendarValue([
      formatInputToDateObject(value[0], timezoneValue),
      formatInputToDateObject(value[1], timezoneValue),
    ]);
  };

  function onClickApply() {
    const appliedValue: DateRangeValue = [startTime?.valueOf() || null, endTime?.valueOf() || null];

    onChangeProp?.(appliedValue);
    onChangeTimezoneProp?.(timezoneValue);
    setResetValue(appliedValue);
    setResetTimezone(timezoneValue);
    handleClose();
    setError(false);
  }

  function onClickReset() {
    onChangeProp?.(resetValue);
    onChangeTimezoneProp?.(resetTimezone);
    setTimezoneValue(resetTimezone);
    setStartTime(resetValue[0] ? startOfMinute(new Date(resetValue[0])) : null);
    setEndTime(resetValue[1] ? startOfMinute(new Date(resetValue[1])) : null);
    setCalendarValue([
      formatInputToDateObject(resetValue[0], resetTimezone),
      formatInputToDateObject(resetValue[1], resetTimezone),
    ]);
    setInputValue(
      formatRangeInputToPlaceholder([resetValue[0], resetValue[1]], {
        format: DEFAULT_DATE_TIME_MASK,
      }),
    );
    handleClose();
    setError(false);
  }

  useEffect(() => {
    setError(!!restProps.error);
  }, [restProps.error]);

  const onChangeTimezone = (v: string | null) => {
    const previousTimezone = timezoneValue || DEFAULT_TIMEZONE;
    setTimezoneValue(v);
    if (startTime) {
      const zonedStart = toZonedTime(startTime, previousTimezone);
      setStartTime(
        setZonedDate({
          timezone: v,
          date: calendarValue[0] || new DateObject(),
          time: `${getHours(zonedStart)}:${getMinutes(zonedStart)}`,
        }),
      );
    }
    if (endTime) {
      const zonedEnd = toZonedTime(endTime, previousTimezone);
      setEndTime(
        setZonedDate({
          timezone: v,
          date: calendarValue[1] || new DateObject(),
          time: `${getHours(zonedEnd)}:${getMinutes(zonedEnd)}`,
        }),
      );
    }
  };

  const onResetInput = () => {
    onChangeProp?.([null, null]);
    setResetValue(defaultValue || [null, null]);
    setError(false);
  };

  const startTimeForPicker = startTime
    ? toZonedTime(startTime, timezoneValue || DEFAULT_TIMEZONE)
    : null;
  const endTimeForPicker = endTime ? toZonedTime(endTime, timezoneValue || DEFAULT_TIMEZONE) : null;

  return (
    <>
      <DatePickerMaskInput
        {...restProps}
        disabled={disabled}
        error={error || restProps?.error}
        label={label}
        mask={DEFAULT_DATE_TIME_RANGE_MASK}
        onChange={onChangeInput}
        onClick={(e) => {
          if (disabled) {
            return;
          }

          handleClick(e);
        }}
        onResetInput={onResetInput}
        placeholder={DEFAULT_DATE_TIME_RANGE_PLACEHOLDER}
        size={size}
        value={inputValue}
        variant={variant}
      />
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableAutoFocus={true}
        id={id}
        onClose={() => {
          handleClose();
          onClickReset();
        }}
        open={open}
      >
        <PopoverLayoutStyled>
          <PopoverCalendarContainerStyled>
            {displayShortcuts && (
              <PopoverListStyled>
                <DatePickerShortcuts
                  onClick={onClickShortcut}
                  shortcuts={shortcuts}
                  timezone={timezoneValue || DEFAULT_TIMEZONE}
                />
              </PopoverListStyled>
            )}
            <PopoverContentStyled>
              <DatePickerTimezoneToolbar
                end={calendarValue[1]}
                onChangeTimezone={onChangeTimezone}
                start={calendarValue[0]}
                timezone={timezoneValue || DEFAULT_TIMEZONE}
                title={translate('app.datePicker.dateTimeRangeSelection')}
              />
              <PopoverDatesStyled>
                <PopoverCalendarContainerStyled>
                  <Calendar
                    gap="30px"
                    maxDate={
                      (maxDate && formatInputToDateObject(maxDate, timezoneValue)) || undefined
                    }
                    minDate={
                      (minDate && formatInputToDateObject(minDate, timezoneValue)) || undefined
                    }
                    numberOfMonths={2}
                    onChange={onChangeCalendar}
                    range
                    value={calendarValue}
                  />
                </PopoverCalendarContainerStyled>
                <PopoverTimesStyled>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePickerTime
                      label={translate('app.datePicker.timeFrom')}
                      onChange={onChangeStartTime}
                      timezone={timezoneValue || DEFAULT_TIMEZONE}
                      value={startTimeForPicker}
                    />
                    <DatePickerTime
                      label={translate('app.datePicker.timeTo')}
                      onChange={onChangeEndTime}
                      timezone={timezoneValue || DEFAULT_TIMEZONE}
                      value={endTimeForPicker}
                    />
                  </LocalizationProvider>
                </PopoverTimesStyled>
              </PopoverDatesStyled>
            </PopoverContentStyled>
          </PopoverCalendarContainerStyled>
          <DatePickerActions>
            <Button onClick={onClickReset} variant="outlined">
              {translate('app.buttons.cancel')}
            </Button>
            <Button onClick={onClickApply} variant="contained">
              {translate('app.buttons.confirm')}
            </Button>
          </DatePickerActions>
        </PopoverLayoutStyled>
      </Popover>
    </>
  );
};
